/**
 * KHIYARSA (AI): client for POST /api/app/ai/generate (SSE) and
 * GET /api/app/ai/info. Same-origin fetches — the session cookie rides
 * along; no keys or provider details ever reach the browser.
 */
import type { WireDiagnostic } from "../markdown/diagnostics";

export interface GenerateRequest {
  prompt: string;
  family?: string;
  detail?: "concise" | "detailed";
  language?: "auto" | "en" | "ar";
  icons?: boolean;
  sourceMaterial?: string;
}

export type GenerateEvent =
  | {
      type: "status";
      stage: "generating" | "validating" | "repairing";
      attempt: number;
      maxAttempts: number;
    }
  | {
      type: "result";
      outcome: "valid" | "invalid" | "error";
      errorCode: string | null;
      kmd: string | null;
      diagnostics: WireDiagnostic[];
      attempts: number;
    }
  | { type: "error"; code: string };

export class AiApiError extends Error {
  constructor(public status: number, public code: string) {
    super(code);
  }
}

export interface AiInfo {
  enabled: boolean;
  provider: string;
  model: string;
  limits: {
    promptMaxChars: number;
    sourceMaxChars: number;
    dailyQuota: number;
    ratePerMin: number;
  };
}

export const fetchAiInfo = async (): Promise<AiInfo> => {
  const res = await fetch("/api/app/ai/info");
  if (!res.ok) {
    throw new AiApiError(res.status, await errorCode(res));
  }
  return res.json();
};

const errorCode = async (res: Response): Promise<string> => {
  try {
    const body = await res.json();
    return typeof body.error === "string" ? body.error : `http_${res.status}`;
  } catch {
    return `http_${res.status}`; // nginx 429/502 pages are not JSON
  }
};

export const generateDiagram = async (
  request: GenerateRequest,
  onEvent: (event: GenerateEvent) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const res = await fetch("/api/app/ai/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    signal,
  });
  if (!res.ok || !res.body) {
    throw new AiApiError(res.status, await errorCode(res));
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const line = chunk
        .split("\n")
        .find((candidate) => candidate.startsWith("data: "));
      if (!line) {
        continue;
      }
      const payload = line.slice(6);
      if (payload === "[DONE]") {
        return;
      }
      try {
        onEvent(JSON.parse(payload) as GenerateEvent);
      } catch {
        // malformed frame — ignore; the terminal [DONE]/result governs
      }
    }
  }
};
